export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

// Robust parser that converts various timestamp formats to a local Date
export const parseToDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;

  const str = String(value).trim();

  // If already an ISO string with timezone (Z or +/- offset), rely on native parsing
  if (/T.*(Z|[+\-]\d{2}:?\d{2})$/.test(str)) {
    const d = new Date(str);
    return isNaN(d.getTime()) ? null : d;
  }

  // Match: YYYY-MM-DD HH:MM[:SS][.uuuuuu]
  const sqlLike = str.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,6}))?)?$/);
  if (sqlLike) {
    const [, y, m, d, hh, mm, ss = '0', micro = '0'] = sqlLike;
    const year = Number(y);
    const monthIndex = Number(m) - 1;
    const day = Number(d);
    const hours = Number(hh);
    const minutes = Number(mm);
    const seconds = Number(ss);
    // Convert microseconds to milliseconds by truncation/padding to 3 digits
    const ms = Number((micro || '0').slice(0, 3).padEnd(3, '0'));
    const local = new Date(year, monthIndex, day, hours, minutes, seconds, ms);
    return isNaN(local.getTime()) ? null : local;
  }

  // Match: YYYY-MM-DDTHH:MM[:SS][.sss] (no timezone) → treat as local
  const isoNoTz = str.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,6}))?)?$/);
  if (isoNoTz) {
    const [, y, m, d, hh, mm, ss = '0', frac = '0'] = isoNoTz;
    const year = Number(y);
    const monthIndex = Number(m) - 1;
    const day = Number(d);
    const hours = Number(hh);
    const minutes = Number(mm);
    const seconds = Number(ss);
    const ms = Number((frac || '0').slice(0, 3).padEnd(3, '0'));
    const local = new Date(year, monthIndex, day, hours, minutes, seconds, ms);
    return isNaN(local.getTime()) ? null : local;
  }

  // Fallback to native Date
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
};

export const formatDate = (dateInput) => {
  if (!dateInput) return '';

  // If already a Date, format directly in local timezone
  if (dateInput instanceof Date) {
    return dateInput.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  const str = String(dateInput).trim();

  // Extract YYYY-MM-DD to avoid implicit UTC conversion
  const match = str.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, yearStr, monthStr, dayStr] = match;
    const year = Number(yearStr);
    const monthIndex = Number(monthStr) - 1; // JS months are 0-based
    const day = Number(dayStr);
    const localDate = new Date(year, monthIndex, day);
    return localDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  // Fallback for full ISO strings
  const d = new Date(str);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

export const formatTime = (timeInput) => {
  if (!timeInput) return '';

  // If already a Date, format time component
  if (timeInput instanceof Date) {
    return timeInput.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  }

  const str = String(timeInput).trim();

  // Handle ISO strings with timezone info (from backend)
  if (str.includes('T') && (str.includes('Z') || str.includes('+') || str.includes('-'))) {
    const d = new Date(str);
    if (!isNaN(d.getTime())) {
      return d.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    }
  }

  // If it's a full datetime string, parse to local and return HH:MM
  if (str.includes('T')) {
    const d = parseToDate(str);
    if (d) {
      return d.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    }
  }

  // Support HH:MM, HH:MM:SS, HH:MM:SS.mmmmmm
  const match = str.match(/^(\d{1,2}):(\d{2})/);
  if (match) {
    const [, hoursStr, minutesStr] = match;
    const hours = String(parseInt(hoursStr, 10)).padStart(2, '0');
    const minutes = String(parseInt(minutesStr, 10)).padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  // Fallback to Date parsing if needed
  const d = new Date(`1970-01-01T${str}`);
  if (isNaN(d.getTime())) return str;
  return d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

export const formatDateTime = (date) => {
  if (!date) return '';
  
  // Handle ISO strings with timezone info (from backend)
  if (typeof date === 'string' && (date.includes('T') && (date.includes('Z') || date.includes('+') || date.includes('-')))) {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  }
  
  // Fallback to existing logic for other formats
  const d = parseToDate(date);
  if (!d) return '';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

export const formatTimeAgo = (date) => {
  if (!date) return '';
  const d = parseToDate(date);
  if (!d) return '';
  const seconds = Math.floor((Date.now() - d.getTime()) / 1000);
  
  let interval = seconds / 31536000;
  if (interval > 1) return Math.floor(interval) + ' years ago';
  
  interval = seconds / 2592000;
  if (interval > 1) return Math.floor(interval) + ' months ago';
  
  interval = seconds / 86400;
  if (interval > 1) return Math.floor(interval) + ' days ago';
  
  interval = seconds / 3600;
  if (interval > 1) return Math.floor(interval) + ' hours ago';
  
  interval = seconds / 60;
  if (interval > 1) return Math.floor(interval) + ' minutes ago';
  
  return Math.floor(seconds) + ' seconds ago';
}; 